"use client";

import { useLocale, useTranslations } from "next-intl";
import { type Locale } from "@/i18n/config";
import { getLegalDocs } from "@/lib/legal";
import { Footer } from "@/components/Footer";
import LocaleSync from "@/components/LocaleSync";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";

// Shared legal-documents view. Two routes render it with different chrome:
//  - /legal         → app context: logo links to the app dashboard, no breadcrumb.
//  - /legal/public  → landing context: logo links back to the landing home and a
//                     breadcrumb (PsyPractice › Legal Documents) signals that.
// The document content itself is identical (single source: getLegalDocs).
export function LegalView({
  logoHref,
  logoExternal = false,
  showBreadcrumb = false,
  legalBase = "/legal",
  stickyChrome = false,
}: {
  logoHref: string;
  logoExternal?: boolean;
  showBreadcrumb?: boolean;
  legalBase?: string;
  // When true (landing context) the top bar and the bottom legal-links footer
  // stay pinned to the viewport while the document body scrolls between them.
  stickyChrome?: boolean;
}) {
  const t = useTranslations("legal");
  const locale = useLocale() as Locale;
  const docs = getLegalDocs(locale);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)]">
      {/* Landing (ayrı subdomain) ?lang=tr|en ile gelirse onu NEXT_LOCALE'e yaz. */}
      <LocaleSync />
      {/* Top bar */}
      <header className={`border-b border-[var(--border)] bg-[var(--surface)]${stickyChrome ? " sticky top-0 z-30" : ""}`}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <Logo href={logoHref} external={logoExternal} size={18} className="flex items-center shrink-0" />
            {showBreadcrumb && (
              <>
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                  className="text-[var(--text-muted)] shrink-0" aria-hidden="true"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
                <span className="text-sm font-medium text-[var(--text-secondary)] truncate">
                  {t("title")}
                </span>
              </>
            )}
          </div>
          <LanguageSwitcher className="relative shrink-0" />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-5 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
            {t("title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{t("intro")}</p>
        </div>

        {/* Table of contents */}
        <nav className="mb-10 rounded-2xl border border-[var(--border)] bg-[var(--surface-alt)] p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-3">
            {t("toc")}
          </p>
          <ul className="space-y-1.5">
            {docs.map((d) => (
              <li key={d.id}>
                <a href={`#${d.id}`} className="text-sm text-[var(--accent)] hover:underline">
                  {d.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Documents */}
        <div className="space-y-12">
          {docs.map((doc) => (
            <section key={doc.id} id={doc.id} className="scroll-mt-24">
              <h2 className="text-2xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
                {doc.title}
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{doc.description}</p>
              <p className="text-xs text-[var(--text-muted)] mt-2">
                {t("updated")}: {doc.updated}
              </p>

              <div className="mt-6 space-y-6">
                {doc.sections.map((s, i) => (
                  <div key={i}>
                    {s.heading && (
                      <h3 className="text-base font-semibold text-[var(--text-primary)] mb-2" style={{ fontFamily: "var(--font-lora)" }}>
                        {s.heading}
                      </h3>
                    )}
                    {s.paras?.map((p, j) => (
                      <p key={j} className="text-sm text-[var(--text-secondary)] leading-relaxed mb-2 last:mb-0">
                        {p}
                      </p>
                    ))}
                    {s.bullets && (
                      <ul className="mt-2 space-y-2">
                        {s.bullets.map((b, j) => (
                          <li key={j} className="flex gap-2.5 text-sm text-[var(--text-secondary)] leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-[var(--text-muted)] italic mt-12 leading-relaxed">{t("disclaimer")}</p>
      </main>

      <Footer legalBase={legalBase} className={stickyChrome ? "sticky bottom-0 z-30" : ""} />
    </div>
  );
}
